/**
 * The verb for pressing something, in the words of the hand that does it: "Tap" on a touch screen,
 * "Click" with a mouse (owner, 2026-10-05: "A lot of interactions only make sense on mobile"). Read
 * from `(pointer: coarse)` at the moment the words are drawn, the same test the field-focused rules
 * in styles.css use, so a phone reads exactly what it read before. The tour's copy keeps the
 * peon's "tap" (shared/tour-copy.ts): that is his voice, not an instruction.
 */
export type PressWord = "Tap" | "Click";

/** True on a touch screen (a phone, a tablet); false with a mouse or trackpad, and wherever it cannot be asked. */
export function coarsePointer(): boolean {
  try {
    return typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;
  } catch {
    return false;
  }
}

/** "Tap" for a finger, "Click" for a mouse. Pass `coarse` to decide it without asking the browser (tests). */
export function pressWord(coarse: boolean = coarsePointer()): PressWord {
  return coarse ? "Tap" : "Click";
}

/** The faint line over a sheet's dim that closes it: "Tap to close" on a phone, "Click to close" with a mouse. */
export function closeHint(coarse: boolean = coarsePointer()): string {
  return `${pressWord(coarse)} to close`;
}

/** The photo viewer's first-few-opens hint: the gestures a finger has, or the ones a mouse has. */
export function zoomHint(coarse: boolean = coarsePointer()): string {
  return coarse ? "Pinch or double-tap to zoom" : "Scroll or double-click to zoom";
}
